import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { adaptExternalIcon, Box, IconButton, PressableBox } from '@opendoor/bricks-next';
import { IconX } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'framer-motion';

import { CenteredLayout } from '../StandaloneText';
import { trapFocus } from './trapFocus';

const backdropVariants = {
  hidden: {
    opacity: 0,
    transition: {
      ease: [0.55, 0, 0.15, 1],
      delay: 0.5,
      duration: 0.2,
    },
  },
  visible: {
    opacity: 1,
    transition: {
      ease: [0.55, 0, 0.15, 1],
      duration: 0.2,
    },
  },
};

const boxVariants = {
  hidden: {
    y: '100%',
    transition: {
      ease: [0.55, 0, 0.15, 1],
      duration: 0.5,
    },
  },
  visible: {
    y: 0,
    transition: {
      ease: [0.55, 0, 0.15, 1],
      duration: 0.75,
    },
  },
};

interface ISheetContentProps {
  entry: any;
  setIsVisible: (isVisible: boolean) => void;
}

interface ISheetProps extends ISheetContentProps {
  exitComplete: () => void;
  isVisible?: boolean;
}

const SheetContent = ({ entry, setIsVisible }: ISheetContentProps) => {
  const dialogId = `dialog-${Math.floor(Math.random() * 100 + 1)}-${entry.sys.id}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const onComplete = (definition: any) => {
    // Only focus on main sheet container when the visible animation is complete
    // or it messes up the animation
    if (definition === 'visible') {
      containerRef.current?.focus();
    }
  };

  return (
    <div role="presentation" onKeyDown={(e) => trapFocus(e, containerRef)}>
      <Box
        display="block"
        bottom={0}
        top={0}
        left={0}
        right={0}
        pt="$20"
        px="$4"
        pb="$0"
        zIndex={3000}
        $largerThanSM={{ pt: '$28', px: '$4', pb: '$0' }}
        $platform-web={{
          position: 'fixed',
          display: 'grid',
          gridTemplateRows: '1fr',
          gridTemplateColumns: '1fr',
          overflow: 'auto',
          overscrollBehaviorY: 'contain',
        }}
      >
        <motion.div
          onAnimationComplete={(definition: any) => {
            onComplete(definition);
          }}
          variants={boxVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          style={{ gridArea: '1 / 1 / -1 / -1', zIndex: 10, position: 'relative' }}
        >
          <Box
            display="block"
            tabIndex={-1}
            ref={containerRef}
            backgroundColor="$backgroundPrimary"
            maxWidth="$360"
            mx="auto"
            pt="$16"
            px="$4"
            pb="$8"
            role="dialog"
            aria-labelledby={dialogId}
            aria-modal={true}
            borderTopLeftRadius="$8x"
            borderTopRightRadius="$8x"
            minHeight="100%"
            $largerThanSM={{ pt: '$37', px: '$4', pb: '$8' }}
            $platform-web={{
              outline: '0',
            }}
          >
            <IconButton
              analyticsName=""
              aria-label="Close"
              onPress={() => setIsVisible(false)}
              size="large"
              icon={{ Component: adaptExternalIcon(IconX), label: 'Close' }}
              backgroundColor="$backgroundTertiary"
              position="absolute"
              right="$4"
              top="$4"
            />
            <CenteredLayout entry={entry} dialogId={dialogId} />
          </Box>
        </motion.div>
        <motion.div
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          style={{ gridArea: '1 / 1 / -1 / -1', zIndex: 0 }}
        >
          <PressableBox
            analyticsName=""
            onPress={() => setIsVisible(false)}
            aria-hidden={true}
            bottom={0}
            top={0}
            left={0}
            right={0}
            aria-label="Close sheet"
            $platform-web={{
              position: 'fixed',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
            }}
          />
        </motion.div>
      </Box>
    </div>
  );
};

const Sheet = ({ entry, isVisible, setIsVisible, exitComplete }: ISheetProps) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    document.body.classList.toggle('has-sheet', isVisible);
  }, [isVisible]);

  const escFunction = useCallback((event: { key: string }) => {
    if (event.key === 'Escape') {
      setIsVisible(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  return mounted
    ? createPortal(
        <AnimatePresence initial={false} onExitComplete={() => exitComplete()}>
          {isVisible && (
            <motion.div key={`sheet-${entry.sys.id}`}>
              <SheetContent entry={entry} setIsVisible={setIsVisible} />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )
    : null;
};

export default Sheet;
